import {
  ChevronIcon,
  ContentSwitcher,
  Field,
  IconButton,
  Modal,
  Switch,
  TokenSearchInput,
  type ContentSwitcherOption,
  type FilterGroup,
} from "@carbon6/c6-frontend-design";
import { useId, useState, useSyncExternalStore, type ReactNode } from "react";
import { HatModalHeader } from "@/components/modal";
import { DatasetFilter } from "./DatasetFilter";
import { DATASET_FILTER_PRESETS, facetSection, type DatasetFilterPreset } from "./datasetFilters";
import type { FilterBookmark } from "./datasetFilterBookmarks";
import { FacetControl } from "./FacetControl";
import type { DatasetFilterController } from "./DatasetFilterController";
import type { DatasetMatchMode } from "./labelFilterQuery";
// 조합 방식 문구는 레일 배지(MatchModeChip)와 함께 쓴다. 문구는 MatchModeChip 이 소유한다.
import { combineHints } from "./MatchModeChip";
import styles from "./DatasetFilterSheet.module.css";

// 필터 조합 방식 세그먼트다: AND(기본, labels_all) / OR(labels_any·filter_match=any).
// 선택한 조건(facet 값·generic 라벨·축)을 모두 만족(AND)할지, 하나라도 만족(OR)할지 정한다. 아래의
// 고정 힌트는 선택된 모드만 설명한다. 그래서 옵션 툴팁으로 반대 모드의 설명도 고르기 전에 볼 수
// 있게 한다(문구는 combineHints 를 함께 쓴다).
const matchModeOptions: readonly ContentSwitcherOption[] = [
  { value: "all", label: "모두 만족 (AND)", tooltip: combineHints.all },
  { value: "any", label: "하나라도 (OR)", tooltip: combineHints.any },
];

// 시트에 나열하는 항목이다. 프리셋(★만 있다. 값 변경은 레일 칩 또는 대응 facet 행이 한다) 또는 facet(★ + 컨트롤)이다.
type SheetItem =
  | { entry: FilterBookmark; kind: "preset"; preset: DatasetFilterPreset }
  | { entry: FilterBookmark; kind: "facet"; group: FilterGroup };

// 전체 필터를 관련 축끼리 카드로 나눈다. 여기 없는 key 가 카탈로그에 있으면 "기타" 카드에 넣어
// 빠지지 않게 한다.
const FACET_CARDS: readonly { title: string; keys: readonly string[] }[] = [
  {
    // environment·arms 는 프리셋 전용(datasetFilters.ts section: "preset")이다. 그래서 시트에도
    // 컨트롤을 두지 않는다. 프리셋(RealWorld/시뮬레이션·양팔/한팔)과 중복되기 때문이다.
    title: "데이터 속성",
    keys: ["task", "source", "recording_mode", "action_source"],
  },
  {
    title: "하드웨어",
    keys: ["embodiment", "camera_channel", "camera_count", "tip", "end_effector"],
  },
  { title: "품질 검토", keys: ["episode_quality", "episode_quality_candidate"] },
];

// 선택지 옆 수(c6 count 배지)의 모집단 설명이다. facets 계약(available + 폴더, 다른 조건과 독립)을 문장으로 쓴다.
const FACET_COUNT_CAPTION =
  "선택지 옆 수는 이 폴더의 사용 가능한 Dataset 중 그 값을 가진 수이며, 다른 조건과 무관합니다.";

export type DatasetFilterSheetProps = {
  controller: DatasetFilterController;
  open: boolean;
  onClose: () => void;
  // 레일에 항상 표시하는 북마크(순서 = 레일 표시 순서)와 토글 핸들러다. DatasetFilter 가 소유한다.
  bookmarks: readonly FilterBookmark[];
  onToggleBookmark: (entry: FilterBookmark) => void;
};

// 고급 필터 시트다. 전체 필터 카탈로그를 화면 전환(c6 Modal)으로 표시한다. 각 항목의 ★ 북마크가
// 레일에 항상 표시할지 정한다. facet 값 편집, 라벨 조건(key:value 토큰), 조합 방식을 한 화면에서
// 다룬다. 선택 상태는 컨트롤러가 소유하고, 바뀌면 즉시 커밋한다. 푸터의 완료는 확인하고 닫는
// 버튼이다. 초기화는 서비스 앱이 선언한 검색어·facet·라벨·조합 기본값을 복원한다(북마크는 유지).
export function DatasetFilterSheet({
  controller,
  open,
  onClose,
  bookmarks,
  onToggleBookmark,
}: DatasetFilterSheetProps): ReactNode {
  const titleId = useId();
  const previewTitleId = useId();
  const settingsTitleId = useId();
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const groups = snapshot.filter.groups;
  const selection = snapshot.filter.selection;
  // 카드 접힘 상태다. 시트가 열려 있는 동안만 유지하는 로컬 UI 상태다(기본값은 모두 펼침).
  const [collapsedCards, setCollapsedCards] = useState<ReadonlySet<string>>(new Set());
  const toggleCard = (id: string): void => {
    setCollapsedCards((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const renderCard = (id: string, children: ReactNode, hint?: string): ReactNode => {
    const isCollapsed = collapsedCards.has(id);
    return (
      <section key={id} className={styles.section}>
        <button
          type="button"
          className={styles.cardHead}
          aria-expanded={!isCollapsed}
          onClick={() => toggleCard(id)}
        >
          <span className={styles.sectionTitle}>{id}</span>
          <ChevronIcon
            className={styles.chevron}
            data-collapsed={isCollapsed ? "true" : undefined}
          />
        </button>
        {isCollapsed ? null : (
          <>
            {hint ? <p className={styles.sectionHint}>{hint}</p> : null}
            {children}
          </>
        )}
      </section>
    );
  };

  // 그룹은 고정이다. 프리셋 카드에는 use-case 프리셋을 둔다. facet 은 FACET_CARDS 의 관련 축 카드로
  // 나눈다. ★ 는 항목을 옮기지 않고 제자리에서 체크 상태만 바꾼다. 그래서 사용자가 위치를 기억할 수 있다.
  const presetItems: readonly SheetItem[] = DATASET_FILTER_PRESETS.map(
    (preset): SheetItem => ({ entry: `preset:${preset.id}`, kind: "preset", preset }),
  );
  const toFacetItem = (group: FilterGroup): SheetItem => ({
    entry: `facet:${group.value}`,
    kind: "facet",
    group,
  });
  const facetCards = FACET_CARDS.map((card) => ({
    title: card.title,
    items: card.keys
      .map((key) => groups.find((group) => group.value === key))
      .filter((group): group is FilterGroup => group !== undefined)
      .map(toFacetItem),
  })).filter((card) => card.items.length > 0);
  // FACET_CARDS 에 없는 key 가 카탈로그에 있으면 "기타" 에 넣어 빠지지 않게 한다. 프리셋 전용
  // facet(environment·arms)은 카탈로그에 있지만 컨트롤 대상이 아니므로 "기타" 에 넣지 않는다.
  const coveredKeys = new Set(FACET_CARDS.flatMap((card) => card.keys));
  const leftoverItems = groups
    .filter((group) => !coveredKeys.has(group.value) && facetSection(group.value) !== "preset")
    .map(toFacetItem);

  const renderItem = (item: SheetItem): ReactNode => {
    const bookmarked = bookmarks.includes(item.entry);
    const label = item.kind === "preset" ? item.preset.label : item.group.label;
    return (
      <div key={item.entry} className={styles.itemRow}>
        <button
          type="button"
          className={styles.star}
          data-on={bookmarked ? "true" : undefined}
          aria-pressed={bookmarked}
          aria-label={`${label} 북마크 ${bookmarked ? "해제" : "등록"}`}
          onClick={() => onToggleBookmark(item.entry)}
        >
          {bookmarked ? "★" : "☆"}
        </button>
        {item.kind === "preset" ? (
          <span className={styles.presetLabel}>
            <item.preset.Icon className={styles.presetIcon} aria-hidden="true" />
            <span>{item.preset.label}</span>
            <span className={styles.presetHint}>프리셋</span>
          </span>
        ) : (
          <FacetControl
            controller={controller}
            fullWidth
            group={item.group}
            selectedValues={selection[item.group.value] ?? []}
            disabled={snapshot.loading}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      // 3열 카드 그리드가 들어가도록 폭을 lg(48rem)보다 넓히고, 높이는 고정한다. 그래서 카드를
      // 접었다 펴도 모달 크기가 바뀌지 않는다(내부 스크롤과 여백만 바뀐다).
      style={{ maxWidth: "min(94vw, 64rem)", height: "min(90vh, 54rem)" }}
      maxHeight="min(90vh, 54rem)"
      aria-labelledby={titleId}
    >
      <HatModalHeader title="고급 필터" titleId={titleId} close />
      <Modal.Body>
        {/* 프리뷰다. 시트가 가린 필터 레일을 그대로 표시한다. 같은 컨트롤러를 구독하므로 아래에서
            조건을 고치면 레일이 어떻게 바뀌는지 여기서 즉시 보인다. 스코프는 페이지의 레일이
            소유한다. 그래서 여기서는 북마크 스코프를 바꾸지 않는다. */}
        <section className={styles.preview} aria-labelledby={previewTitleId}>
          <h3 className={styles.areaTitle} id={previewTitleId}>
            미리보기
          </h3>
          <DatasetFilter controller={controller} ownsBookmarkScope={false} />
        </section>
        <hr className={styles.previewDivider} />

        <h3 className={styles.areaTitle} id={settingsTitleId}>
          필터 설정
        </h3>
        <div className={styles.body} role="group" aria-labelledby={settingsTitleId}>
          {facetCards.map((card) =>
            renderCard(
              card.title,
              <div className={styles.items}>
                {card.items.map((item) => (
                  <div key={item.entry} className={styles.items}>
                    {item.kind === "facet" && item.group.value === "episode_quality" ? (
                      <p className={styles.sectionHint}>기록된 결함</p>
                    ) : null}
                    {item.kind === "facet" && item.group.value === "episode_quality_candidate" ? (
                      <p className={styles.qualityHint}>자동 품질 후보 · 확인이 필요한 에피소드</p>
                    ) : null}
                    {renderItem(item)}
                  </div>
                ))}
              </div>,
              card.title === "품질 검토"
                ? "결함 기록과 자동 분석 후보는 독립된 조건입니다."
                : undefined,
            ),
          )}
          {leftoverItems.length > 0
            ? renderCard(
                "기타",
                <div className={styles.items}>{leftoverItems.map(renderItem)}</div>,
              )
            : null}
        </div>
        {snapshot.facetCountsShown ? (
          <p className={styles.countCaption}>{FACET_COUNT_CAPTION}</p>
        ) : null}
        <hr className={styles.previewDivider} />
        <div className={styles.body}>
          {renderCard(
            "빠른 탐색",
            <div className={styles.items}>{presetItems.map(renderItem)}</div>,
            "★ 항목은 목록에서도 항상 표시됩니다. 조건은 선택 즉시 반영됩니다.",
          )}
          <div className={styles.col}>
            {renderCard(
              "조합 방식",
              <>
                <ContentSwitcher
                  size="sm"
                  label="필터 조합 방식"
                  options={matchModeOptions}
                  value={snapshot.matchMode}
                  disabled={snapshot.loading}
                  onValueChange={(mode) => controller.setMatchMode(mode as DatasetMatchMode)}
                />
                <p className={styles.hint}>{combineHints[snapshot.matchMode]}</p>
              </>,
            )}
            <section className={styles.section}>
              <Field label="필터 조건 기억하기" layout="spread">
                <Switch
                  checked={snapshot.rememberFilters}
                  onChange={(event) => controller.setRememberFilters(event.target.checked)}
                  aria-label="필터 조건 기억하기"
                />
              </Field>
              <p className={styles.sectionHint}>
                켜면 선택 조건과 조합 방식을 다음 방문에도 유지합니다. 검색어는 저장하지 않습니다.
              </p>
            </section>
          </div>
          {renderCard(
            "라벨 조건",
            <TokenSearchInput
              inputSize="sm"
              width="100%"
              inputValue={snapshot.labelTokenInput}
              selectedTokens={snapshot.labelTokens}
              options={snapshot.labelTokenOptions}
              placeholder="key:value 형식으로 입력"
              aria-label="라벨 조건"
              emptyMessage="입력과 일치하는 라벨이 없습니다."
              showClear
              onInputValueChange={(value) => controller.setLabelTokenInput(value)}
              onSelectedTokensChange={(tokens) => controller.setLabelTokens(tokens)}
            />,
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <IconButton
          text="전체 해제"
          variant="ghost"
          disabled={snapshot.loading}
          onClick={() => controller.clearAll()}
        />
        <IconButton
          text="기본값 복원"
          type="button"
          variant="surface"
          disabled={snapshot.loading}
          onClick={() => controller.reset()}
        />
        <IconButton text="완료" type="button" variant="solid" onClick={onClose} />
      </Modal.Footer>
    </Modal>
  );
}
